// src/components/forms/SurveyListAnalyticsItem.js
import React from 'react';
import { UserCog, BarChart3, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';

// Expects surveyInfo (matching SurveyInfo Pydantic model) and refreshData function
function SurveyListAnalyticsItem({ surveyInfo, refreshData }) {
	const surveyTitleDisplay = surveyInfo?.title || 'Untitled Survey';
	const navigate = useNavigate();

	const handleAudienceConfigClick = () => {
		console.log(`Navigating to audience config for survey with ID: ${surveyInfo.survey_id}`);
		navigate(`/dashboard/audience/${surveyInfo.survey_id}`);
	};

	const handleResponseClick = () => {
		console.log(`Navigating to responses for survey with ID: ${surveyInfo.survey_id}`);
		navigate(`/dashboard/responses/${surveyInfo.survey_id}`);
	};

	const handleAnalyticsClick = () => {
		console.log(`Navigating to analytics for survey with ID: ${surveyInfo.survey_id}`);
		navigate(`/dashboard/analytics/${surveyInfo.survey_id}`);

	};

	// Mock data for regional response distribution
	const mockRegionData = [
		{ region: 'Île-de-France', count: 89, color: '#3B82F6' },
		{ region: 'PACA', count: 67, color: '#8B5CF6' },
		{ region: 'Grand Est', count: 45, color: '#10B981' },
		{ region: 'Auvergne', count: 34, color: '#F59E0B' },
		{ region: 'Bourgogne', count: 28, color: '#EF4444' }
	];

	const maxCount = Math.max(...mockRegionData.map(d => d.count));

	return (
		<div className='border shadow-sm rounded-lg p-6 flex flex-col h-full relative min-h-[320px]'>
			<div className='flex justify-between items-center'>
				<h2 className='text-lg font-semibold text-black flex-grow truncate'>{surveyTitleDisplay}</h2>
				<div className="flex items-center gap-1">
					<span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Active</span>
				</div>
			</div>

			<h3 className='text-sm text-gray-500 mt-1 mb-4'>
				ID: {surveyInfo.survey_id} - Created: {new Date(surveyInfo.created_at).toLocaleDateString()}
			</h3>

			<hr className='my-2'></hr>

			{/* Analytics Preview - Mock Histogram */}
			<div className='flex-grow my-4'>
				<div className="flex items-center justify-between mb-3">
					<h4 className='text-md font-medium text-gray-700'>Responses by Region</h4>
					<div className="text-xs text-gray-500 flex items-center">
						<BarChart3 className="h-3 w-3 mr-1" />
						Preview
					</div>
				</div>
				
				<div className='bg-gray-50 p-4 rounded-lg border'>
					{/* Histogram Chart */}
					<div className="space-y-3">
						{mockRegionData.map((item, index) => (
							<div key={index} className="flex items-center">
								{/* Region Label */}
								<div className="w-20 text-xs font-medium text-gray-700 flex-shrink-0">
									{item.region}
								</div>
								
								{/* Bar Container */}
								<div className="flex-1 mx-3">
									<div className="bg-gray-200 rounded-full h-4 relative overflow-hidden">
										<div 
											className="h-full rounded-full transition-all duration-500 ease-out"
											style={{ 
												width: `${(item.count / maxCount) * 100}%`,
												backgroundColor: item.color
											}}
										></div>
									</div>
								</div>
								
								{/* Count Value */}
								<div className="w-8 text-xs font-bold text-right" style={{ color: item.color }}>
									{item.count}
								</div>
							</div>
						))}
					</div>
					
					{/* Chart Footer */}
					<div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-200">
						<span className="text-xs text-gray-500">Total: {mockRegionData.reduce((sum, d) => sum + d.count, 0)} responses</span>
						<span className="text-xs text-gray-400">Last updated: 2h ago</span>
					</div>
				</div>
			</div>

			<hr className='my-2'></hr>

			{/* Action Buttons - All visible without dropdown */}
			<div className='flex justify-between items-center mt-auto gap-2'>
				{/* Primary Action - Analytics */}
				<Button
					onClick={handleAnalyticsClick}
					variant="primary"
					className="flex gap-2 items-center px-3 py-2 border rounded text-sm text-gray-700 hover:bg-gray-100 transition-colors"
				>
					<BarChart3 className='h-4 w-4'/> Analytics
				</Button>

				{/* Secondary Actions */}
				<Button
					onClick={handleResponseClick}
					variant="secondary"
					className="flex gap-2 items-center px-3 py-2 border rounded text-sm text-gray-700 hover:bg-gray-100 transition-colors"
				>
					<MessageSquare className='h-4 w-4'/> Responses
				</Button>

				<Button
					onClick={handleAudienceConfigClick}
					variant="secondary"
					className="flex gap-2 items-center px-3 py-2 border rounded text-sm text-gray-700 hover:bg-gray-100 transition-colors"
				>
					<UserCog className='h-4 w-4'/> Audience
				</Button>
			</div>
		</div>
	);
}

export default SurveyListAnalyticsItem;